import {
  addDays,
  diffDays,
  hhmmToMinutes,
  localDateTime,
  localDateTimeInTimeZone,
  type MilkSession,
  type SessionSetting,
  type TenantSettings,
} from '@dairy/shared';
import { eventsOf, latestOnOrBefore } from './events';
import { deriveStage } from './stage';
import { live, type DomainAnimal, type DomainEvent, type DomainMilkRecord, type DomainUsage } from './types';
import { activeWithdrawal } from './withdrawal';

/** MR-2: enabled sessions in day order. */
export function enabledSessions(s: Pick<TenantSettings, 'sessions'>): SessionSetting[] {
  return [...s.sessions].sort((a, b) => hhmmToMinutes(a.time) - hhmmToMinutes(b.time));
}

/** MR-1: the enabled session whose time is closest to now. `nowLocal` = "YYYY-MM-DDTHH:mm" (farm time). */
export function currentSession(s: Pick<TenantSettings, 'sessions'>, nowLocal: string): MilkSession {
  const nowMin = hhmmToMinutes(nowLocal.slice(11, 16));
  let best = enabledSessions(s)[0]!;
  let bestDiff = Infinity;
  for (const ses of enabledSessions(s)) {
    const diff = Math.abs(hhmmToMinutes(ses.time) - nowMin);
    if (diff < bestDiff) {
      best = ses;
      bestDiff = diff;
    }
  }
  return best.session;
}

/** MR-7: session time + lateEntryHours, as a local "YYYY-MM-DDTHH:mm" (may fall on the next day). */
export function sessionCutoff(
  date: string,
  session: MilkSession,
  s: Pick<TenantSettings, 'sessions' | 'lateEntryHours'>,
): string | null {
  const ses = s.sessions.find((x) => x.session === session);
  if (!ses) return null;
  return localDateTime(date, hhmmToMinutes(ses.time) + s.lateEntryHours * 60);
}

/** D-1: animals with stage MILKING on `date`, sorted by tag. */
export function milkingCowsOn<A extends DomainAnimal>(
  animals: readonly A[],
  eventsByAnimal: ReadonlyMap<string, readonly DomainEvent[]>,
  date: string,
  s: Pick<TenantSettings, 'calfMaxAgeMonths'>,
): A[] {
  return live(animals)
    .filter((a) => a.sex === 'FEMALE' && deriveStage(a, eventsByAnimal.get(a.id) ?? [], date, s) === 'MILKING')
    .sort(compareTags);
}

export function compareTags(a: { tag: string }, b: { tag: string }): number {
  return a.tag.localeCompare(b.tag, 'en', { numeric: true, sensitivity: 'base' });
}

export interface AutoWithheld {
  reason: 'COLOSTRUM' | 'TREATMENT';
  until: string;
}

/** D-6: automatic withheld milk for a cow on a date. TREATMENT wins over COLOSTRUM. */
export function autoWithheld(
  animal: DomainAnimal,
  events: readonly DomainEvent[],
  date: string,
  s: Pick<TenantSettings, 'colostrumDays'>,
): AutoWithheld | null {
  const evs = eventsOf(events, animal.id);
  const w = activeWithdrawal(evs, date);
  if (w) return { reason: 'TREATMENT', until: w.until };
  const calving = latestOnOrBefore(evs, 'CALVING', date);
  if (calving) {
    const dim = diffDays(date, calving.date);
    if (dim >= 0 && dim <= s.colostrumDays - 1) {
      return { reason: 'COLOSTRUM', until: addDays(calving.date, s.colostrumDays - 1) };
    }
  }
  return null;
}

export interface SessionAverage {
  avgMl: number;
  count: number;
  sumMl: number;
}

/** D-7: mean of the cow's records for `session` from date−7 to date−1. */
export function sessionAverage(
  records: readonly DomainMilkRecord[],
  animalId: string,
  session: MilkSession,
  date: string,
): SessionAverage {
  const from = addDays(date, -7);
  let sum = 0;
  let count = 0;
  for (const r of records) {
    if (r.deletedAt || r.animalId !== animalId || r.session !== session) continue;
    if (r.date < from || r.date >= date) continue;
    sum += r.ml;
    count += 1;
  }
  return { avgMl: count ? sum / count : 0, count, sumMl: sum };
}

/** D-7: drop if value < threshold × avg (strict), and at least 3 previous records. Integer arithmetic. */
export function isMilkDrop(valueMl: number, avg: SessionAverage, threshold = 0.8): boolean {
  if (avg.count < 3 || avg.sumMl <= 0) return false;
  const permille = Math.round(threshold * 1000);
  return valueMl * avg.count * 1000 < permille * avg.sumMl;
}

/** "↓ 25%" — percentage below the average, rounded. */
export function dropPercent(valueMl: number, avgMl: number): number {
  if (avgMl <= 0) return 0;
  return Math.round((1 - valueMl / avgMl) * 100);
}

export interface DailyTotals {
  producedMl: number;
  withheldMl: number;
  calfMl: number;
  homeMl: number;
  spoiledMl: number;
  usageMl: number;
  saleableMl: number;
}

/** D-8 for one day. */
export function dailyTotals(
  records: readonly DomainMilkRecord[],
  usage: readonly DomainUsage[] | DomainUsage | null | undefined,
  date: string,
): DailyTotals {
  let produced = 0;
  let withheld = 0;
  for (const r of records) {
    if (r.deletedAt || r.date !== date) continue;
    produced += r.ml;
    if (r.withheld) withheld += r.ml;
  }
  const list = usage == null ? [] : Array.isArray(usage) ? usage : [usage];
  const u = list.find((x) => !x.deletedAt && x.date === date);
  const calf = u?.calfMl ?? 0;
  const home = u?.homeMl ?? 0;
  const spoiled = u?.spoiledMl ?? 0;
  const usageMl = calf + home + spoiled;
  return {
    producedMl: produced,
    withheldMl: withheld,
    calfMl: calf,
    homeMl: home,
    spoiledMl: spoiled,
    usageMl,
    saleableMl: produced - withheld - usageMl,
  };
}

/** MR-6: totals per session for one day. */
export function sessionTotals(records: readonly DomainMilkRecord[], date: string): Record<MilkSession, number> {
  const out: Record<MilkSession, number> = { MORNING: 0, MIDDAY: 0, EVENING: 0, SESSION_4: 0, SESSION_5: 0, SESSION_6: 0 };
  for (const r of records) if (!r.deletedAt && r.date === date) out[r.session] += r.ml;
  return out;
}

export interface MissedSession {
  date: string;
  session: MilkSession;
  missingCount: number;
  missingAnimalIds: string[];
}

/**
 * MR-7: sessions in the last 7 days (today included) whose cutoff has passed and where at least one
 * MILKING cow has no record. Days before the farm's first milk record are ignored (recording had not started).
 * `nowLocal` = "YYYY-MM-DDTHH:mm" in farm time.
 */
export function missedSessions(
  animals: readonly DomainAnimal[],
  events: readonly DomainEvent[],
  records: readonly DomainMilkRecord[],
  s: Pick<TenantSettings, 'sessions' | 'lateEntryHours' | 'calfMaxAgeMonths'>,
  nowLocal: string,
): MissedSession[] {
  const liveRecords = live(records);
  if (liveRecords.length === 0) return [];
  let firstRecordDate = liveRecords[0]!.date;
  for (const r of liveRecords) if (r.date < firstRecordDate) firstRecordDate = r.date;
  const recorded = new Set(liveRecords.map((r) => `${r.animalId}|${r.date}|${r.session}`));
  const byAnimal = new Map<string, DomainEvent[]>();
  for (const e of live(events)) {
    const l = byAnimal.get(e.animalId);
    if (l) l.push(e);
    else byAnimal.set(e.animalId, [e]);
  }
  const today = nowLocal.slice(0, 10);
  const out: MissedSession[] = [];
  for (let i = 6; i >= 0; i--) {
    const date = addDays(today, -i);
    if (date < firstRecordDate) continue;
    const cows = milkingCowsOn(animals, byAnimal, date, s);
    for (const ses of enabledSessions(s)) {
      const cutoff = sessionCutoff(date, ses.session, s)!;
      if (nowLocal <= cutoff) continue;
      const missing = cows.filter((c) => !recorded.has(`${c.id}|${date}|${ses.session}`)).map((c) => c.id);
      if (missing.length > 0) {
        out.push({ date, session: ses.session, missingCount: missing.length, missingAnimalIds: missing });
      }
    }
  }
  return out;
}

/**
 * MR-8: a record first saved more than `lateEntryHours` after its session time is late.
 * Computed from the device's first-save instant (UTC) converted to the farm's time zone.
 */
export function isLateEntry(
  clientCreatedAtUtc: string | Date,
  date: string,
  session: MilkSession,
  s: Pick<TenantSettings, 'sessions' | 'lateEntryHours'>,
  timeZone: string,
): boolean {
  const cutoff = sessionCutoff(date, session, s);
  if (!cutoff) return false;
  const instant = typeof clientCreatedAtUtc === 'string' ? new Date(clientCreatedAtUtc) : clientCreatedAtUtc;
  if (Number.isNaN(instant.getTime())) return false;
  const savedLocal = localDateTimeInTimeZone(timeZone, instant);
  return savedLocal > cutoff;
}
